/**
 * @file: useVehicleInfoLoader.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { kNullVIN } from '@euler/functions/constants';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { captureException } from '@sentry/react';
import { useOrderFlowContext } from '../../../OrderFlowContext';

export function useVehicleInfoLoader() {
  const {
    lastVehicleInfo$,
    vehicleImageUrl$,
    formController: { form },
  } = useOrderFlowContext();
  const { vehicleInfoService } = useServiceFactory();
  return usePersistFn(async (vin: string) => {
    if (vin === kNullVIN) return;

    form.vehicleInfo.setLoading(true);
    try {
      const { vinInfo } = await vehicleInfoService.getVinInfo(vin);
      if (vinInfo) {
        lastVehicleInfo$.next(vinInfo);
        form.vehicleInfo.update({
          vehicleName: vinInfo.canonicalName,
          vehicleImageUrl: vinInfo.imgUrl ?? undefined,
          vehicleBrandName: vinInfo.brand!,
          vehicleBrandLogo: vinInfo.brandLogo ?? undefined,
          vehicleManufacturer: vinInfo.manufacturer ?? undefined,
          vehicleFuelType: vinInfo.fuelType ?? undefined,
          vehicleModel: vinInfo.seriesName ?? vinInfo.modelName ?? undefined,
          vehicleModelYear: vinInfo.modelYear ?? undefined,
          vehicleDisplacement: vinInfo.displacement ?? undefined,
        });
        vehicleImageUrl$.next(vinInfo.imgUrl ?? undefined);
      }
      form.vehicleInfo.setLoading(false);
    } catch (e) {
      captureException(e);
      form.vehicleInfo.update(undefined);
      form.vehicleInfo.setError(
        '获取车辆信息失败, 请检查输入/识别的VIN码是否正确, 然后点击重试, 如果查询依然失败，您可以进行手动选择',
      );
    }
  });
}
